import React, { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const TESTIMONIALS = [
  {
    quote: "What we are building inside SKI Axis is the governance layer the GCC actually needs — and the founders here are exactly the kind we want at the table.",
    who: "Mr. Abu Nahyan Al Nuaimi",
    role: "Founder · Atlantis Real Estate · Cohort 1 Faculty",
  },
  {
    quote: "Cohort 1 is intentionally small — twenty founders, six weeks, one framework. The math is uncomfortable on purpose.",
    who: "Dr. Theroshen Rafahël",
    role: "Co-Founder · SKI Asia Pacific · Cap Tables Faculty",
  },
];

function CountdownPill({ deadlineISO }) {
  const [now, setNow] = useState(new Date());
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);
  const deadline = new Date(deadlineISO);
  const ms = deadline - now;
  if (ms <= 0) return <span style={{ color: '#D63031', fontWeight: 700 }}>Applications Closed</span>;
  const days = Math.floor(ms / (1000 * 60 * 60 * 24));
  const hrs = Math.floor((ms % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  return (
    <span data-testid="cohort-countdown">
      <strong style={{ color: '#C8A84B', letterSpacing: '.04em' }}>{days}d {hrs}h</strong> until applications close
    </span>
  );
}

export default function WebinarThankYou() {
  const { slug } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = params.get('session_id');

  const [tx, setTx] = useState(null);
  const [webinar, setWebinar] = useState(null);
  const [stack, setStack] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upsellLoading, setUpsellLoading] = useState(false);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let alive = true;
    async function load() {
      try {
        const [statusRes, webinarRes] = await Promise.all([
          sessionId ? axios.get(`${API}/payments/status/${sessionId}`) : Promise.resolve({ data: null }),
          axios.get(`${API}/webinars/${slug}`),
        ]);
        if (!alive) return;
        setTx(statusRes.data);
        setWebinar(webinarRes.data);
        // Fetch stack tier if we have a customer email
        const email = statusRes.data?.metadata?.email || statusRes.data?.email;
        if (email) {
          try {
            const { data: s } = await axios.get(`${API}/credit/webinar-stack`, { params: { email } });
            if (alive) setStack(s);
          } catch (stackErr) {
            // Non-fatal: stack tier is a cosmetic upgrade; surface to console for debugging.
            console.warn('[WebinarThankYou] credit stack fetch failed:', stackErr?.response?.status || stackErr?.message);
          }
        }
      } catch (e) {
        if (!alive) return;
        setErr(e?.response?.data?.detail || 'Could not load your payment.');
      } finally {
        if (alive) setLoading(false);
      }
    }
    load();
    return () => { alive = false; };
  }, [sessionId, slug]);

  async function applyCreditAndContinue() {
    const email = tx?.metadata?.email || tx?.email;
    const fullName = tx?.metadata?.full_name || tx?.full_name;
    if (!email || !fullName) {
      // Fall back to /apply with the coupon code attached
      navigate(`/apply?credit=WEBINAR149&from_webinar=${slug}`);
      return;
    }
    setUpsellLoading(true);
    try {
      const { data } = await axios.post(`${API}/payments/checkout/cohort-1`, {
        origin_url: window.location.origin,
        email: email,
        full_name: fullName,
        apply_webinar_credit: true,
        source_webinar_slug: slug,
      });
      if (data?.url) {
        window.location.href = data.url;
        return;
      }
      throw new Error('No checkout url returned');
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Could not start the Cohort 1 checkout.');
      setUpsellLoading(false);
    }
  }

  useEffect(() => {
    if (webinar?.title) document.title = `Thank you · ${webinar.title}`;
    else document.title = 'Thank you · SKI Axis Webinar';
  }, [webinar]);

  if (loading) {
    return (
      <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#FAF7F0', fontFamily: 'Georgia, serif', color: '#3A4A5C' }}>
        <em>Confirming your payment…</em>
      </div>
    );
  }

  const amountPaid = tx?.amount || 149;
  const activeCreditAed = stack?.active_tier?.amount_aed || 149;
  const nextTierAed = stack?.next_tier?.amount_aed;
  const webinarsToNext = stack?.webinars_to_next_tier || 0;
  const stackCount = stack?.paid_webinars ?? 1;
  const finalCohortPrice = 6900 - activeCreditAed;

  return (
    <>

      <style>{`
        :root { --cream:#FAF7F0; --navy:#0D1B2A; --gold:#C8A84B; --gold-soft:rgba(200,168,75,.14); --gold-line:#E8DFC9; }
        .wt-page { background:var(--cream); min-height:100vh; padding:48px 20px 80px; font-family:Georgia, 'Times New Roman', serif; color:var(--navy); }
        .wt-shell { max-width:980px; margin:0 auto; }
        .wt-confirm { display:flex; align-items:center; gap:14px; padding:14px 20px; background:rgba(46,184,138,.10); border-left:3px solid #2EB88A; color:#0D1B2A; font-size:14px; margin-bottom:32px; }
        .wt-confirm strong { color:#1F8B6A; letter-spacing:.06em; text-transform:uppercase; font-size:11px; margin-right:6px; }
        .wt-hero { padding:48px 0 28px; text-align:left; border-bottom:1px solid var(--gold-line); margin-bottom:36px; }
        .wt-eyebrow { font-size:11px; font-weight:700; letter-spacing:.32em; color:#8A6A14; text-transform:uppercase; margin-bottom:14px; }
        .wt-h1 { font-family:'Cormorant Garamond', Georgia, serif; font-size:44px; line-height:1.1; font-weight:700; letter-spacing:-.014em; margin:0 0 18px; color:var(--navy); }
        .wt-h1 em { font-style:italic; color:var(--gold); font-weight:400; }
        .wt-sub { font-size:17px; line-height:1.65; color:#3A4A5C; max-width:640px; }

        .wt-grid { display:grid; grid-template-columns:1fr 1fr; gap:28px; margin-bottom:48px; }
        @media (max-width:760px) { .wt-grid { grid-template-columns:1fr; } .wt-h1 { font-size:32px; } }

        .wt-card { background:#FFFFFF; border:1px solid var(--gold-line); padding:28px 26px; }
        .wt-card-h { font-size:11px; font-weight:700; letter-spacing:.32em; color:#8A6A14; text-transform:uppercase; margin-bottom:14px; }
        .wt-card-body strong { font-family:'Cormorant Garamond', Georgia, serif; font-size:22px; color:var(--navy); display:block; line-height:1.25; margin-bottom:8px; }
        .wt-card ul { list-style:none; padding:0; margin:14px 0 0; }
        .wt-card li { padding:8px 0; font-size:14px; line-height:1.6; color:#3A4A5C; border-top:1px dashed rgba(13,27,42,.08); padding-left:22px; position:relative; }
        .wt-card li:first-child { border-top:none; }
        .wt-card li::before { content:'→'; position:absolute; left:0; top:8px; color:var(--gold); font-weight:700; }

        .wt-card-next { background:var(--navy); color:var(--cream); border:none; }
        .wt-card-next .wt-card-h { color:var(--gold); }
        .wt-card-next .wt-card-body strong { color:var(--cream); }
        .wt-card-next li { color:rgba(250,247,240,.82); border-top-color:rgba(250,247,240,.10); }

        .wt-credit-box { margin:0 0 36px; padding:30px 32px; background:linear-gradient(135deg, var(--gold-soft) 0%, rgba(200,168,75,.04) 100%); border:1.5px solid var(--gold); border-radius:0; position:relative; overflow:hidden; }
        .wt-credit-eyebrow { font-size:11px; font-weight:700; letter-spacing:.32em; color:#8A6A14; text-transform:uppercase; margin-bottom:12px; }
        .wt-credit-headline { font-family:'Cormorant Garamond', Georgia, serif; font-size:30px; color:var(--navy); line-height:1.18; font-weight:700; margin:0 0 16px; max-width:640px; }
        .wt-credit-headline em { font-style:italic; color:var(--gold); font-weight:400; }
        .wt-credit-math { font-size:14.5px; line-height:1.7; color:#3A4A5C; margin-bottom:22px; max-width:640px; }
        .wt-credit-math s { color:rgba(13,27,42,.45); }
        .wt-credit-math .wt-final { font-family:'Cormorant Garamond', Georgia, serif; font-size:22px; color:var(--navy); font-weight:700; }

        .wt-cta-row { display:flex; flex-wrap:wrap; gap:14px; align-items:center; }

        /* Stack ladder */
        .wt-ladder { display:grid; grid-template-columns:repeat(3,1fr); gap:14px; margin:8px 0 18px; }
        @media (max-width:560px) { .wt-ladder { grid-template-columns:1fr; } }
        .wt-rung { display:flex; align-items:center; gap:14px; padding:14px 16px; background:rgba(255,255,255,.55); border:1px solid rgba(13,27,42,.10); transition:all .25s ease; }
        .wt-rung-step { width:32px; height:32px; border-radius:50%; background:#FFFFFF; border:1.5px solid rgba(13,27,42,.20); display:flex; align-items:center; justify-content:center; font-weight:700; font-size:14px; color:#7A8190; flex-shrink:0; }
        .wt-rung-aed { font-family:'Cormorant Garamond',Georgia,serif; font-size:18px; font-weight:700; color:#0D1B2A; line-height:1; }
        .wt-rung-lbl { font-size:11px; letter-spacing:.10em; color:#7A8190; text-transform:uppercase; margin-top:4px; }
        .wt-rung-earned { background:rgba(46,184,138,.10); border-color:rgba(46,184,138,.40); }
        .wt-rung-earned .wt-rung-step { background:#2EB88A; color:#FFFFFF; border-color:#2EB88A; }
        .wt-rung-active { background:#FFFFFF; border:1.5px solid var(--gold); box-shadow:0 0 0 4px rgba(200,168,75,.12); }
        .wt-rung-active .wt-rung-step { background:var(--gold); color:var(--navy); border-color:var(--gold); }
        .wt-rung-active .wt-rung-aed { color:var(--gold); }

        .wt-next-tier { font-size:13.5px; line-height:1.65; color:#3A4A5C; padding:14px 18px; background:rgba(13,27,42,.05); border-left:2px solid var(--navy); margin:0 0 22px; }
        .wt-next-tier strong { color:var(--navy); }
        .wt-cta-gold { background:var(--gold); color:var(--navy); padding:16px 26px; font-size:12px; font-weight:700; letter-spacing:.18em; text-transform:uppercase; border:none; cursor:pointer; font-family:Georgia, serif; transition:all .25s ease; }
        .wt-cta-gold:hover:not(:disabled) { background:#B8961E; transform:translateY(-1px); }
        .wt-cta-gold:disabled { opacity:.55; cursor:wait; }
        .wt-cta-ghost { background:transparent; color:var(--navy); border:1px solid rgba(13,27,42,.30); padding:16px 22px; font-size:11.5px; font-weight:700; letter-spacing:.16em; text-transform:uppercase; cursor:pointer; font-family:Georgia, serif; transition:all .25s ease; }
        .wt-cta-ghost:hover { border-color:var(--navy); }
        .wt-countdown { font-size:13px; color:#3A4A5C; }

        .wt-quotes { display:grid; grid-template-columns:1fr 1fr; gap:24px; margin:24px 0 36px; }
        @media (max-width:760px) { .wt-quotes { grid-template-columns:1fr; } }
        .wt-quote { padding:24px 26px; background:#FFFFFF; border-left:3px solid var(--gold); }
        .wt-quote p { font-family:'Cormorant Garamond', Georgia, serif; font-size:17px; line-height:1.5; color:var(--navy); margin:0 0 14px; font-style:italic; }
        .wt-quote-who { font-size:11.5px; font-weight:700; letter-spacing:.06em; color:var(--navy); }
        .wt-quote-role { font-size:12px; color:#7A8190; font-style:italic; margin-top:2px; }

        .wt-foot { display:flex; flex-wrap:wrap; gap:18px; justify-content:space-between; padding-top:32px; border-top:1px solid var(--gold-line); font-size:13px; color:#7A8190; }
        .wt-foot a { color:#8A6A14; text-decoration:none; }
        .wt-err { background:rgba(214,48,49,.08); border-left:3px solid #D63031; color:#D63031; padding:12px 16px; font-size:13px; margin-bottom:20px; }
      `}</style>

      <div className="wt-page" data-testid="webinar-thankyou-page">
        <div className="wt-shell">

          {/* Confirmation banner */}
          <div className="wt-confirm" data-testid="payment-confirmed">
            <strong>Payment Received</strong>
            <span>AED {amountPaid} · Stripe transaction <code style={{ fontSize: 12 }}>{(sessionId || '').slice(0, 18)}…</code></span>
          </div>

          {err && <div className="wt-err">{err}</div>}

          {/* Hero */}
          <div className="wt-hero">
            <div className="wt-eyebrow">Thank you · You're In</div>
            <h1 className="wt-h1">A seat is reserved <em>in your name</em>.</h1>
            <p className="wt-sub">
              Your audience access for <strong>{webinar?.title || 'the upcoming webinar'}</strong>
              {webinar?.date_label ? <> on <strong>{webinar.date_label}</strong></> : ''} is confirmed.
              You'll receive a branded calendar invite (.ics) and the Zoom link in your inbox within the next two minutes.
            </p>
          </div>

          {/* What you just unlocked / What's next */}
          <div className="wt-grid">
            <div className="wt-card">
              <div className="wt-card-h">What You Just Unlocked</div>
              <div className="wt-card-body">
                <strong>{webinar?.title || 'Live Audience Access'}</strong>
                <ul>
                  <li>90-minute live institutional briefing</li>
                  <li>Live Zoom + LinkedIn Live · live audience Q&amp;A</li>
                  <li>On-demand replay within 24 hours</li>
                  <li>Branded calendar invite synced to your calendar</li>
                  <li>Faculty: Dr. Neha · Dr. Ross · Shanshan · Abu Nahyan · Shir Sagie</li>
                </ul>
              </div>
            </div>
            <div className="wt-card wt-card-next">
              <div className="wt-card-h">What's Next · Cohort 1</div>
              <div className="wt-card-body">
                <strong>SKI Axis Accelerator · Cohort 1</strong>
                <ul>
                  <li>12-week governance-first programme · 1 Jul → 12 Aug 2026</li>
                  <li>20 founders · weekly 1:1 with all 5 mentors</li>
                  <li>Sentinel governance audit + IFZA company registration</li>
                  <li>Demo Day · UAE family offices + sovereign capital</li>
                  <li>Microsoft / Google / AWS startup credits unlocked</li>
                </ul>
              </div>
            </div>
          </div>

          {/* The credit upsell — tiered Stack the Credits */}
          <div className="wt-credit-box" data-testid="cohort-credit-upsell">
            <div className="wt-credit-eyebrow">
              {stack ? `Stack the Credits · Tier ${Math.min(stackCount, 3)} of 3` : 'Limited Credit · Webinar Buyer Bonus'}
            </div>
            <h2 className="wt-credit-headline">
              Apply your <em>AED {activeCreditAed.toLocaleString()}</em> credit to Cohort 1.
            </h2>
            <p className="wt-credit-math">
              Cohort 1 seat: <s>AED 6,900</s> &nbsp;–&nbsp; AED {activeCreditAed.toLocaleString()} stack credit &nbsp;=&nbsp;
              <span className="wt-final"> AED {finalCohortPrice.toLocaleString()}</span> · pay today, lock the seat.
            </p>

            {/* Stack ladder visualisation */}
            {stack && (
              <div className="wt-ladder" data-testid="stack-ladder">
                {[
                  { count: 1, aed: 149, label: '1 webinar' },
                  { count: 2, aed: 400, label: '2 webinars' },
                  { count: 3, aed: 1000, label: '3+ webinars' },
                ].map((rung) => {
                  const earned = stackCount >= rung.count;
                  const isActive = stack.active_tier?.amount_aed === rung.aed;
                  return (
                    <div key={rung.count} className={`wt-rung ${earned ? 'wt-rung-earned' : ''} ${isActive ? 'wt-rung-active' : ''}`} data-testid={`ladder-rung-${rung.count}`}>
                      <div className="wt-rung-step">{earned ? '✓' : rung.count}</div>
                      <div>
                        <div className="wt-rung-aed">AED {rung.aed.toLocaleString()} off</div>
                        <div className="wt-rung-lbl">{rung.label}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {nextTierAed && webinarsToNext > 0 && (
              <p className="wt-next-tier" data-testid="next-tier-hint">
                <strong>{webinarsToNext} more webinar{webinarsToNext > 1 ? 's' : ''}</strong> and your credit jumps from <strong>AED {activeCreditAed.toLocaleString()}</strong> to <strong>AED {nextTierAed.toLocaleString()}</strong>. The series pays you back.
              </p>
            )}

            <div className="wt-cta-row">
              <button
                onClick={applyCreditAndContinue}
                disabled={upsellLoading}
                className="wt-cta-gold"
                data-testid="apply-credit-cta"
              >
                {upsellLoading ? 'Starting checkout…' : `Apply AED ${activeCreditAed.toLocaleString()} · Continue to Cohort 1 →`}
              </button>
              <Link to="/apply" className="wt-cta-ghost" data-testid="apply-form-cta">
                Read the application brief →
              </Link>
              <span className="wt-countdown">
                <CountdownPill deadlineISO="2026-06-30T23:59:00+04:00" />
              </span>
            </div>
          </div>

          {/* Testimonials */}
          <div className="wt-eyebrow" style={{ marginTop: 24, marginBottom: 14 }}>What the faculty say</div>
          <div className="wt-quotes">
            {TESTIMONIALS.map((t, i) => (
              <div key={i} className="wt-quote" data-testid={`faculty-quote-${i}`}>
                <p>"{t.quote}"</p>
                <div className="wt-quote-who">{t.who}</div>
                <div className="wt-quote-role">{t.role}</div>
              </div>
            ))}
          </div>

          {/* Foot */}
          <div className="wt-foot">
            <div>
              SKI Asia Pacific FZCO · Dubai · <a href="https://skiaxis.com">skiaxis.com</a>
            </div>
            <div>
              Need help? Email <a href="mailto:accelerator@skiasiapacific.com">accelerator@skiasiapacific.com</a>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
